import { type ErrorResult } from '@pkgs/api/types';

import { isRecord } from './object';
import { type HttpError, isHttpError } from './httpError';

type ErrorDetails = {
  traceId: string;
  details: string;
};

const isErrorDetails = (data: unknown): data is ErrorDetails => {
  return isRecord(data) && 'traceId' in data && 'details' in data;
};

export const createError = <T = unknown>(error: unknown): HttpError<T> | Error => {
  if (isHttpError(error) || error instanceof Error) {
    return error;
  } else if (isRecord(error) && 'message' in error) {
    return new Error(error.message as string);
  } else if (typeof error === 'string') {
    return new Error(error);
  } else {
    return new Error('An unknown error occurred.');
  }
};

export const getErrorMessage = (error: HttpError | Error): string => {
  if (isHttpError(error) && error.response) {
    const { data, status } = error.response;
    return `HttpError: ${status} - ${isErrorDetails(data) ? data.details : JSON.stringify(data)}`;
  } else {
    return error.message;
  }
};

export const getErrorResult = <T = unknown, M = unknown>(error: unknown, metadata?: M): ErrorResult<T, M> => {
  const parsedError = createError<T>(error);
  return {
    status: 'error',
    error: parsedError,
    statusCode: isHttpError(parsedError) ? (parsedError.response?.status ?? 500) : 500,
    data: isHttpError(parsedError) ? (parsedError.response?.data as T) : undefined,
    errorMessage: getErrorMessage(parsedError),
    metadata,
  };
};
